/**
 * Dot colours for venue chips and bands. There is no venue colour in the data, so a venue takes
 * the avatar hue at its position in the venue list (the six `--avatar-N-foreground` tokens, each
 * readable on the card in both themes). Literal class names so Tailwind generates them. The dot is
 * decoration only: the venue name is always printed beside it.
 */
export const VENUE_DOT_CLASSES = [
  'bg-avatar-0-foreground',
  'bg-avatar-1-foreground',
  'bg-avatar-2-foreground',
  'bg-avatar-3-foreground',
  'bg-avatar-4-foreground',
  'bg-avatar-5-foreground',
] as const;

export function venueColorIndex(
  venues: { id: string }[],
  venueId: string | null | undefined,
): number | null {
  if (!venueId) return null;
  const i = venues.findIndex((v) => v.id === venueId);
  return i === -1 ? null : i % VENUE_DOT_CLASSES.length;
}

/** Courts grouped by venue in venue order (stable inside a venue); courts with no known venue last. */
export function orderCourtsByVenue<T extends { venueId?: string | null }>(
  courts: T[],
  venues: { id: string }[],
): T[] {
  const rank = (c: T) => {
    const i = venues.findIndex((v) => v.id === (c.venueId ?? null));
    return i === -1 ? venues.length : i;
  };
  return courts
    .map((c, i) => ({ c, i, r: rank(c) }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((x) => x.c);
}

export interface VenueBand {
  venueId: string | null;
  name: string | null;
  colorIndex: number | null;
  /** Number of court columns the band spans. */
  span: number;
}

/** One band per run of adjacent columns in the same venue, for the header row above the court names. */
export function venueBands(
  columns: { venueId?: string | null }[],
  venues: { id: string; name: string }[],
): VenueBand[] {
  const bands: VenueBand[] = [];
  for (const col of columns) {
    const venue = venues.find((v) => v.id === (col.venueId ?? null));
    const id = venue?.id ?? null;
    const last = bands[bands.length - 1];
    if (last && last.venueId === id) {
      last.span += 1;
    } else {
      bands.push({
        venueId: id,
        name: venue?.name ?? null,
        colorIndex: venueColorIndex(venues, id),
        span: 1,
      });
    }
  }
  return bands;
}
